<script setup lang="ts">
// inbox-101 — Минимальный public layout для prerender.
// inbox-336 — tap-targets ≥44px на всех nav-элементах.
// inbox-402 — hamburger menu на mobile (<768px): public-nav скрыта,
//   кнопка ☰ открывает full-screen overlay со списком разделов.
import { ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const isMobileNavOpen = ref(false);
const route = useRoute();

watch(() => route.fullPath, () => {
  isMobileNavOpen.value = false;
});

function toggleMobileNav(): void {
  isMobileNavOpen.value = !isMobileNavOpen.value;
}
function closeMobileNav(): void {
  isMobileNavOpen.value = false;
}
</script>

<template>
  <div class="app-public" :class="{ 'nav-open': isMobileNavOpen }">
    <header class="public-topbar">
      <a class="brand" href="/">
        <span class="brand-mark">L</span>
        <span class="brand-name">link-platform</span>
      </a>
      <nav class="public-nav">
        <a href="/features">Возможности</a>
        <a href="/pricing">Тарифы</a>
        <a href="/ai">AI</a>
        <a href="/integrations">Интеграции</a>
        <a href="/docs">Документация</a>
        <a href="/cases">Кейсы</a>
        <a href="/about">О компании</a>
      </nav>
      <div class="public-cta">
        <a class="link" href="/login">Войти</a>
        <a class="btn-cta" href="/signup">Начать бесплатно</a>
      </div>
      <button
        type="button"
        class="nav-hamburger"
        :aria-expanded="isMobileNavOpen"
        aria-label="Открыть меню"
        @click="toggleMobileNav"
      >
        <svg v-if="!isMobileNavOpen" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
        <svg v-else viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
          <line x1="6" y1="6" x2="18" y2="18" />
          <line x1="6" y1="18" x2="18" y2="6" />
        </svg>
      </button>
    </header>

    <Transition name="mobile-nav">
      <nav
        v-if="isMobileNavOpen"
        class="mobile-nav-overlay"
        aria-label="Мобильная навигация"
        @click.self="closeMobileNav"
      >
        <div class="mobile-nav-card">
          <a href="/features" @click="closeMobileNav">Возможности</a>
          <a href="/pricing" @click="closeMobileNav">Тарифы</a>
          <a href="/ai" @click="closeMobileNav">AI</a>
          <a href="/integrations" @click="closeMobileNav">Интеграции</a>
          <a href="/docs" @click="closeMobileNav">Документация</a>
          <a href="/cases" @click="closeMobileNav">Кейсы</a>
          <a href="/about" @click="closeMobileNav">О компании</a>
          <div class="mobile-nav-divider" />
          <a href="/login" class="mobile-nav-login" @click="closeMobileNav">Войти</a>
          <a href="/signup" class="mobile-nav-cta" @click="closeMobileNav">Начать бесплатно</a>
        </div>
      </nav>
    </Transition>

    <main class="public-main">
      <slot />
    </main>

    <footer class="public-footer">
      <div class="footer-inner">
        <div class="footer-brand">
          <span class="brand-mark">L</span>
          <span class="brand-name">link-platform</span>
        </div>
        <nav class="footer-nav">
          <a href="/features">Возможности</a>
          <a href="/pricing">Тарифы</a>
          <a href="/docs">Документация</a>
          <a href="/about">О компании</a>
          <a href="/terms">Условия</a>
          <a href="/privacy">Конфиденциальность</a>
        </nav>
        <div class="footer-copy">© 2026 link-platform</div>
      </div>
    </footer>
  </div>
</template>

<style scoped>
.app-public {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #0a0d12;
  color: #e6eaf0;
}
.app-public.nav-open {
  overflow: hidden;
  height: 100vh;
}
.public-topbar {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 16px 32px;
  border-bottom: 1px solid #1f2733;
  position: relative;
  z-index: 31;
  background: #0a0d12;
}
.brand {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #e6eaf0;
  text-decoration: none;
  font-weight: 700;
  min-height: 44px;
}
.brand-mark {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: linear-gradient(135deg, #4f8cff, #b366ff);
  display: grid;
  place-items: center;
  color: #fff;
  font-weight: 800;
  font-size: 14px;
}
.public-nav {
  display: flex;
  gap: 18px;
  margin-left: 8px;
  align-items: center;
}
.public-nav a {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 4px;
  color: #9aa5b1;
  text-decoration: none;
  font-size: 14px;
}
.public-nav a:hover {
  color: #e6eaf0;
}
.public-cta {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 16px;
}
.public-cta .link {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 4px;
  color: #e6eaf0;
  text-decoration: none;
  font-size: 14px;
}
.btn-cta {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #4f8cff, #6f9eff);
  color: #fff;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
}

.nav-hamburger {
  display: none;
  width: 44px;
  height: 44px;
  background: transparent;
  border: 0;
  color: #e6eaf0;
  cursor: pointer;
  border-radius: 8px;
  padding: 0;
  align-items: center;
  justify-content: center;
  margin-left: auto;
}
.nav-hamburger:hover {
  background: #1a1f29;
}

.mobile-nav-overlay {
  display: none;
  position: fixed;
  inset: 64px 0 0 0;
  z-index: 30;
  background: rgba(10, 13, 18, 0.92);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  padding: 24px 18px;
}
.mobile-nav-card {
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: #11161e;
  border: 1px solid #1f2733;
  border-radius: 12px;
  padding: 12px 8px;
}
.mobile-nav-card a {
  display: flex;
  align-items: center;
  min-height: 48px;
  padding: 0 16px;
  color: #e6eaf0;
  text-decoration: none;
  font-size: 15px;
  border-radius: 8px;
}
.mobile-nav-card a:hover,
.mobile-nav-card a:active {
  background: #1a1f29;
}
.mobile-nav-divider {
  height: 1px;
  background: #1f2733;
  margin: 8px 4px;
}
.mobile-nav-login {
  color: #9aa5b1 !important;
}
.mobile-nav-cta {
  background: linear-gradient(135deg, #4f8cff, #6f9eff);
  color: #fff !important;
  font-weight: 600;
  justify-content: center;
}

.mobile-nav-enter-active,
.mobile-nav-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.mobile-nav-enter-from,
.mobile-nav-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.public-main {
  flex: 1;
}
.public-footer {
  border-top: 1px solid #1f2733;
  padding: 32px;
  background: #0a0d12;
}
.footer-inner {
  max-width: 1180px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  text-align: center;
}
.footer-brand {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
}
.footer-nav {
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  justify-content: center;
}
.footer-nav a {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 4px;
  color: #9aa5b1;
  text-decoration: none;
  font-size: 13px;
}
.footer-nav a:hover {
  color: #e6eaf0;
}
.footer-copy {
  font-size: 12px;
  color: #6b7480;
}

/* inbox-402 — mobile: full nav + login link скрыты; hamburger открывает
 * overlay с все 7 nav + Войти + Начать бесплатно. CTA-кнопка остаётся
 * в шапке для одной заметной точки входа. */
@media (max-width: 767px) {
  .public-topbar {
    gap: 8px;
    padding: 10px 16px;
  }
  .public-nav {
    display: none;
  }
  .public-cta .link {
    display: none;
  }
  .public-cta {
    margin-left: 0;
    gap: 8px;
  }
  .nav-hamburger {
    display: inline-flex;
  }
  .mobile-nav-overlay {
    display: block;
  }
}
</style>
